import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Typography } from "@material-ui/core";
import { useSearchParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "./styles";
import EmployeeModal from "./EmployeeModal";
import Employee from "../../../model/employee";
import DateConverter from "../../../utils/DateConverter";
import Misc from "../../../utils/Misc";
import { PrimaryColor } from "../../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  employee: Employee;
  resetList?: () => void;
  isExternal?: boolean;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const EmployeeDetailModal = ({
  employee,
  resetList,
  isExternal,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(employee.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  //TODO: get current project
  useEffect(() => {
    //ApiService.get("")
  });

  useEffect(() => {
    const employeeId = searchParams.get("employeeId");
    if (!open && employeeId && employeeId === employee.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  return (
    <div>
      <Button onClick={handleOpen}>
        {!isExternal ? (
          <MoreVertIcon sx={{ color: PrimaryColor}}/>
        ) : (
          <Typography>{employee.firstName}</Typography>
        )}
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box sx={style}>
          <div className={classes.headerDiv}>
            <Typography className={classes.title}>Employee info</Typography>
            <div className={classes.headerSubDiv}>
              <EmployeeModal employee={employee} resetList={resetList} />
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
          </div>
          <hr />
          <div className={classes.form}>
            <div className={classes.inputDiv}>
              <Typography className={classes.label}>Name</Typography>
              <Typography className={classes.inputDetail}>
                {employee.firstName} {employee.lastName}
              </Typography>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Email</Typography>
                <Typography className={classes.inputDetail}>
                  {employee.email}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Phone</Typography>
                <Typography className={classes.inputDetail}>
                  {Misc.formatPhoneNumber(employee.phone)}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Street</Typography>
                <Typography className={classes.inputDetail}>
                  {employee.street}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>ZIP</Typography>
                <Typography className={classes.inputDetail}>
                  {employee.zip}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>City</Typography>
                <Typography className={classes.inputDetail}>
                  {employee.city}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>State</Typography>
                <Typography className={classes.inputDetail}>
                  {employee.state}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              {employee.itin && (
                <div className={classes.inputDiv}>
                  <Typography className={classes.label}>ITIN</Typography>
                  <Typography className={classes.inputDetail}>
                    {employee.itin}
                  </Typography>
                </div>
              )}
              {employee.socialSecurityNumber && (
                <div className={classes.inputDiv}>
                  <Typography className={classes.label}>SSN</Typography>
                  <Typography className={classes.inputDetail}>
                    {employee.socialSecurityNumber}
                  </Typography>
                </div>
              )}
            </div>
            <div className={classes.inputDiv}>
              <Typography className={classes.label}>Rating</Typography>
              <Typography className={classes.inputDetail}>
                {employee.rating}
              </Typography>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Created At</Typography>
                <Typography className={classes.inputDetail}>
                  {DateConverter.toFullDateAndHours(employee.createdAt)}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Updated At</Typography>
                <Typography className={classes.inputDetail}>
                  {DateConverter.toFullDateAndHours(employee.updatedAt)}
                </Typography>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default EmployeeDetailModal;
